/**
 * fetch-обёртка API клиентского портала с надёжным auto-refresh.
 *
 * Сценарий 401:
 *   1. Запрос упал с 401 → доставём refresh из localStorage
 *   2. POST /api/portal/auth/refresh/ → новый access
 *   3. Сохраняем access, повторяем оригинальный запрос ОДИН раз
 *   4. Если рефреш упал → очищаем токены и редирект на /login?redirect=...
 *
 * Защита от race: один общий refreshPromise — N параллельных 401-запросов
 * вызывают ровно ОДИН рефреш и все ждут его результат.
 *
 * Backend отвечает конвертом { success, message, code, result, errors, meta }.
 * apiClient.get/post/... разворачивают `result`; getRaw отдаёт весь конверт
 * (нужно для пагинации с `meta`). При ошибке бросается
 * { message, code, status, errors, networkError? }.
 */

const API_BASE = '/api'

const STORAGE_KEYS = {
  ACCESS: 'pharmacy_portal_access_token',
  REFRESH: 'pharmacy_portal_refresh_token',
} as const

export interface ApiEnvelope<T> {
  success: boolean
  message?: string
  code?: string
  result: T | null
  errors?: Array<{ field?: string; message: string }> | null
  meta?: { total: number; page: number; limit: number; totalPages?: number } | null
}

interface RequestOptions {
  params?: Record<string, unknown>
  body?: unknown
}

interface ApiError {
  message: string
  code: string | null
  status: number
  errors: Array<{ field?: string; message: string }> | null
  networkError?: boolean
}

// =============================================================================
// Token storage
// =============================================================================

export const tokenStorage = {
  setTokens(access: string, refresh: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS, access)
    localStorage.setItem(STORAGE_KEYS.REFRESH, refresh)
  },
  setAccess(access: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS, access)
  },
  clear(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS)
    localStorage.removeItem(STORAGE_KEYS.REFRESH)
  },
  getAccess(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS)
  },
  getRefresh(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH)
  },
  hasTokens(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.ACCESS)
        && !!localStorage.getItem(STORAGE_KEYS.REFRESH)
  },
}

// =============================================================================
// URL helper
// =============================================================================

function buildUrl(path: string, params?: Record<string, unknown>): string {
  let url = `${API_BASE}${path}`
  if (params) {
    const filtered = Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    )
    if (filtered.length) {
      const qs = new URLSearchParams(
        filtered.map(([k, v]) => [k, String(v)]),
      ).toString()
      url += `?${qs}`
    }
  }
  return url
}

// =============================================================================
// Refresh — singleton promise, защита от race
// =============================================================================

let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise

  refreshPromise = doRefresh()

  try {
    return await refreshPromise
  } finally {
    // Сбрасываем чуть позже — параллельным запросам, которые сейчас в очереди,
    // не нужен ещё один рефреш в тот же тик.
    setTimeout(() => { refreshPromise = null }, 50)
  }
}

async function doRefresh(): Promise<boolean> {
  const refresh = tokenStorage.getRefresh()
  if (!refresh) return false

  try {
    const res = await fetch(`${API_BASE}/portal/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    })

    if (!res.ok) {
      console.warn('[Portal API] Refresh failed:', res.status)
      return false
    }

    const json = await res.json()
    const newAccess = json?.result?.access_token
    if (!newAccess) {
      console.warn('[Portal API] Refresh response missing access_token')
      return false
    }

    tokenStorage.setAccess(newAccess)
    return true
  } catch (e) {
    console.error('[Portal API] Refresh error:', e)
    return false
  }
}

// =============================================================================
// Force logout — очистка токенов + редирект на /login с сохранением URL
// =============================================================================

function forceLogout(): void {
  tokenStorage.clear()

  // Сбросим состояние authStore (динамический импорт — чтобы не было циклов).
  import('@/stores/authStore')
    .then(({ useAuthStore }) => {
      const store = useAuthStore()
      store.$patch({ client: null })
    })
    .catch(() => { /* store ещё не инициализирован — норма */ })

  if (window.location.pathname !== '/login') {
    const redirect = window.location.pathname + window.location.search
    window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`
  }
}

// =============================================================================
// Core request
// =============================================================================

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
  allowRefresh = true,
): Promise<ApiEnvelope<T>> {
  const url = buildUrl(path, options.params)

  function buildInit(): RequestInit {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const access = tokenStorage.getAccess()
    if (access) headers['Authorization'] = `Bearer ${access}`
    const init: RequestInit = { method, headers }
    if (options.body !== undefined) init.body = JSON.stringify(options.body)
    return init
  }

  let response: Response
  try {
    response = await fetch(url, buildInit())
  } catch (e) {
    const err: ApiError = {
      message: 'Нет соединения с сервером',
      code: 'NETWORK_ERROR',
      status: 0,
      errors: null,
      networkError: true,
    }
    throw err
  }

  // 401 → auto-refresh, затем повтор запроса (но не разрешаем повторный рефреш)
  if (response.status === 401 && allowRefresh && tokenStorage.getAccess()) {
    console.debug('[Portal API] 401 received, attempting refresh...')
    const refreshed = await tryRefresh()

    if (refreshed) {
      console.debug('[Portal API] Refresh OK, retrying original request')
      return request<T>(method, path, options, false)
    }

    console.warn('[Portal API] Refresh failed, logging out')
    forceLogout()
    const err: ApiError = {
      message: 'Сессия истекла. Войдите снова.',
      code: 'SESSION_EXPIRED',
      status: 401,
      errors: null,
    }
    throw err
  }

  // Парсим конверт
  let json: ApiEnvelope<T> | null = null
  try {
    json = (await response.json()) as ApiEnvelope<T>
  } catch {
    json = null
  }

  if (!response.ok || !json || json.success === false) {
    const err: ApiError = {
      message: json?.message || `Ошибка запроса (${response.status})`,
      code: json?.code ?? null,
      status: response.status,
      errors: json?.errors ?? null,
    }
    throw err
  }

  return json
}

// =============================================================================
// Public API
// =============================================================================

export const apiClient = {
  /** GET — возвращает развёрнутый result. */
  async get<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    return (await request<T>('GET', path, options)).result as T
  },
  /** GET — возвращает весь конверт (result + meta) — для пагинации. */
  getRaw<T = unknown>(path: string, options?: RequestOptions): Promise<ApiEnvelope<T>> {
    return request<T>('GET', path, options)
  },
  async post<T = unknown>(path: string, body?: unknown): Promise<T> {
    return (await request<T>('POST', path, { body })).result as T
  },
  async patch<T = unknown>(path: string, body?: unknown): Promise<T> {
    return (await request<T>('PATCH', path, { body })).result as T
  },
  async del<T = unknown>(path: string): Promise<T> {
    return (await request<T>('DELETE', path)).result as T
  },
}
